import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Json } from "@/integrations/supabase/types";
import { resourceBlocksSchema } from "@/lib/resource-block-schema";
import { invalidateCached } from "@/lib/server-cache";

export const getLockedResourcesAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const [admin, editor] = await Promise.all([
      context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" }),
      context.supabase.rpc("has_role", { _user_id: context.userId, _role: "editor" }),
    ]);
    if (!admin.data && !editor.data) throw new Error("Du saknar behörighet till låsta sidor.");

    const { ensureLockedResourceRows } = await import("@/lib/locked-resources.server");
    await ensureLockedResourceRows();

    const { data, error } = await context.supabase
      .from("locked_resource_content")
      .select("resource_slug, locale, title, description, content, status, updated_at")
      .order("resource_slug")
      .order("locale");
    if (error) throw new Error(error.message);
    invalidateCached("resource-meta");

    const { resources } = await import("@/lib/resources");
    const { resourceContent } = await import("@/lib/resources-content.server");
    return {
      rows: data ?? [],
      defaults: resources.map((resource) => ({
        slug: resource.slug,
        slugs: { sv: resource.slug, en: resource.slugEn, es: resource.slug },
        titles: { sv: resource.title, en: resource.titleEn, es: resource.titleEn },
        descriptions: { sv: resource.intro, en: resource.introEn, es: resource.introEn },
        content: {
          sv: resourceContent(resource.slug, "sv") ?? [],
          en: resourceContent(resource.slug, "en") ?? [],
          es: resourceContent(resource.slug, "es") ?? [],
        },
      })),
    };
  });

export const saveLockedResource = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({
      resourceSlug: z.string().min(1).max(80),
      locale: z.enum(["sv", "en", "es"]),
      title: z.string().min(1).max(240),
      description: z.string().max(1200),
      content: resourceBlocksSchema,
      status: z.enum(["draft", "published"]),
    }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const [admin, editor] = await Promise.all([
      context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" }),
      context.supabase.rpc("has_role", { _user_id: context.userId, _role: "editor" }),
    ]);
    if (!admin.data && !editor.data) throw new Error("Du saknar behörighet till låsta sidor.");

    const { resources } = await import("@/lib/resources");
    if (!resources.some((resource) => resource.slug === data.resourceSlug)) {
      throw new Error("Guiden finns inte i Kunskapsbanken.");
    }

    const { error } = await context.supabase.from("locked_resource_content").upsert(
      {
        resource_slug: data.resourceSlug,
        locale: data.locale,
        title: data.title,
        description: data.description,
        content: data.content as Json,
        status: data.status,
        updated_by: context.userId,
        updated_by_email: (context.claims as { email?: string }).email ?? "",
      },
      { onConflict: "resource_slug,locale" },
    );
    if (error) throw new Error(error.message);

    await context.supabase.from("editorial_activity").insert({
      entity: "locked_resource_content",
      action: "update",
      summary: `Uppdaterade låst guide: ${data.resourceSlug} (${data.locale})`,
      meta: { resource_slug: data.resourceSlug, locale: data.locale, status: data.status },
      actor_user_id: context.userId,
      actor_email: (context.claims as { email?: string }).email ?? "",
    });
    return { ok: true };
  });
/** Adminförhandsvisning: hämtar guidens innehåll (även utkast) utan e-postlåset. */
export const getLockedResourcePreview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({
      resourceSlug: z.string().trim().min(1).max(80),
      locale: z.enum(["sv", "en", "es"]).default("sv"),
    }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const [admin, editor] = await Promise.all([
      context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" }),
      context.supabase.rpc("has_role", { _user_id: context.userId, _role: "editor" }),
    ]);
    if (!admin.data && !editor.data) return { allowed: false as const, blocks: [], status: "" };

    const { ensureLockedResourceRows } = await import("@/lib/locked-resources.server");
    await ensureLockedResourceRows();

    const { data: row } = await context.supabase
      .from("locked_resource_content")
      .select("content, status")
      .eq("resource_slug", data.resourceSlug)
      .eq("locale", data.locale)
      .maybeSingle();

    return {
      allowed: true as const,
      blocks: Array.isArray(row?.content) ? row.content : [],
      status: row?.status ?? "published",
    };
  });
